import {
  CERT_ARTBOARD_H,
  CERT_ARTBOARD_W,
  CertificateBlock,
  CertificateCanvasDocument,
  CertificateCanvasLayer,
} from './certificateTypes'

function newCertLayerId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `cl-${crypto.randomUUID()}`
  }
  return `cl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function layer(partial: Omit<CertificateCanvasLayer, 'id'>): CertificateCanvasLayer {
  return {id: newCertLayerId(), ...partial}
}

export function defaultCertificateCanvasDocument(
  block: CertificateBlock,
  trainingTitle?: string
): CertificateCanvasDocument {
  const accent = block.certificateAccentColor || '#c1272d'
  const orgName = block.certificateOrganizationName || 'AD ASTRA, INC.'
  const deptName = block.certificateDepartment || 'Learning and Development Department'
  const heading = block.certificateHeading || 'CERTIFICATE OF COMPLETION'
  const subtitle =
    block.certificateSubtitle ||
    'has successfully completed the in-person interpreter training program'
  const duration = block.certificateProgramDuration || '8 Hours (In-Person)'
  const partnership =
    block.certificatePartnershipLine !== undefined
      ? block.certificatePartnershipLine
      : 'conducted in partnership with the Maryland Department of Juvenile Services'
  const primName = block.certificateSignatoryName || 'Elena Petrova-Toolsie'
  const primTitle = block.certificateSignatoryTitle || 'President & CEO, Ad Astra, Inc.'
  const secName = block.certificateSecondSignatoryName || 'Brian Thornton'
  const secTitle = block.certificateSecondSignatoryTitle || 'Training & Quality, Ad Astra, Inc.'
  const secSigUrl = block.certificateSecondSignatureUrl || '/media/logos/signature.png'
  const logoUrl = block.certificateLogoUrl || '/media/logos/Ad_Astra_Logo_Red_prod.png'
  const footer =
    block.certificateFooterText ||
    'Ad Astra, Inc. · 8701 Georgia Avenue, Suite #808 · Silver Spring, MD 20910 · www.ad-astrainc.com'

  const layers: CertificateCanvasLayer[] = [
    layer({
      kind: 'image',
      imageUrl: logoUrl,
      x: 436,
      y: 65,
      w: 250,
      h: 58,
      z: 5,
    }),
    layer({
      kind: 'field',
      fieldKey: 'organizationName',
      text: orgName,
      x: 62,
      y: 132,
      w: 1000,
      h: 22,
      z: 5,
      fontSize: 14,
      fontWeight: '800',
      letterSpacing: '0.08em',
      color: accent,
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'departmentName',
      text: deptName,
      x: 62,
      y: 150,
      w: 1000,
      h: 22,
      z: 5,
      fontSize: 13,
      fontStyle: 'italic',
      color: '#555555',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'heading',
      text: heading,
      x: 62,
      y: 182,
      w: 1000,
      h: 44,
      z: 5,
      fontSize: 34,
      fontWeight: '800',
      letterSpacing: '0.03em',
      color: '#000000',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'shape',
      shapeType: 'line',
      x: 92,
      y: 242,
      w: 940,
      h: 3,
      z: 4,
      backgroundColor: accent,
    }),
    layer({
      kind: 'text',
      text: 'This is to certify that',
      x: 62,
      y: 280,
      w: 1000,
      h: 28,
      z: 5,
      fontSize: 17,
      fontStyle: 'italic',
      fontFamily: 'Georgia, "Times New Roman", serif',
      color: '#333333',
      textAlign: 'center',
    }),
    layer({
      kind: 'field',
      fieldKey: 'learnerName',
      x: 62,
      y: 312,
      w: 1000,
      h: 52,
      z: 6,
      fontSize: 38,
      fontWeight: '700',
      color: '#000000',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'shape',
      shapeType: 'line',
      x: 122,
      y: 368,
      w: 880,
      h: 1,
      z: 4,
      backgroundColor: '#78829d',
    }),
    layer({
      kind: 'field',
      fieldKey: 'subtitle',
      text: subtitle,
      x: 62,
      y: 384,
      w: 1000,
      h: 26,
      z: 5,
      fontSize: 16,
      color: '#181c32',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'trainingTitle',
      text: trainingTitle || '',
      x: 62,
      y: 408,
      w: 1000,
      h: 44,
      z: 6,
      fontSize: 22,
      fontWeight: '700',
      fontStyle: 'italic',
      color: '#000000',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'partnershipLine',
      text: partnership,
      x: 62,
      y: 436,
      w: 1000,
      h: 24,
      z: 5,
      fontSize: 14,
      fontStyle: 'italic',
      color: '#555555',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'text',
      text: 'DATE OF COMPLETION',
      x: 240,
      y: 472,
      w: 260,
      h: 20,
      z: 5,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: '0.04em',
      color: accent,
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'issuedDate',
      x: 240,
      y: 494,
      w: 260,
      h: 24,
      z: 5,
      fontSize: 15,
      color: '#000000',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'text',
      text: 'PROGRAM DURATION',
      x: 622,
      y: 472,
      w: 260,
      h: 20,
      z: 5,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: '0.04em',
      color: accent,
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'programDuration',
      text: duration,
      x: 622,
      y: 494,
      w: 260,
      h: 24,
      z: 5,
      fontSize: 15,
      color: '#000000',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'shape',
      shapeType: 'line',
      x: 135,
      y: 600,
      w: 300,
      h: 1.5,
      z: 4,
      backgroundColor: '#000000',
    }),
    layer({
      kind: 'field',
      fieldKey: 'primarySignatoryName',
      text: primName,
      x: 135,
      y: 608,
      w: 300,
      h: 24,
      z: 5,
      fontSize: 15,
      fontWeight: '700',
      color: '#000000',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'primarySignatoryTitle',
      text: primTitle,
      x: 135,
      y: 632,
      w: 300,
      h: 22,
      z: 5,
      fontSize: 13,
      fontStyle: 'italic',
      color: '#555555',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'image',
      imageUrl: secSigUrl,
      x: 742,
      y: 556,
      w: 160,
      h: 42,
      z: 5,
    }),
    layer({
      kind: 'shape',
      shapeType: 'line',
      x: 673,
      y: 600,
      w: 300,
      h: 1.5,
      z: 4,
      backgroundColor: '#000000',
    }),
    layer({
      kind: 'field',
      fieldKey: 'secondarySignatoryName',
      text: secName,
      x: 673,
      y: 608,
      w: 300,
      h: 24,
      z: 5,
      fontSize: 15,
      fontWeight: '700',
      color: '#000000',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'secondarySignatoryTitle',
      text: secTitle,
      x: 673,
      y: 632,
      w: 300,
      h: 22,
      z: 5,
      fontSize: 13,
      fontStyle: 'italic',
      color: '#555555',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
    layer({
      kind: 'field',
      fieldKey: 'footerText',
      text: footer,
      x: 62,
      y: 712,
      w: 1000,
      h: 22,
      z: 5,
      fontSize: 11,
      color: '#555555',
      textAlign: 'center',
      fontFamily: 'Arial, Helvetica, sans-serif',
    }),
  ]

  return {
    version: 1,
    width: CERT_ARTBOARD_W,
    height: CERT_ARTBOARD_H,
    backgroundColor: '#ffffff',
    theme: 'classic',
    accentColor: accent,
    layers,
  }
}
